import { 
  RodneCisloValidation, 
  IBANValidation,
  SEPAPayment,
  InstitutionalPayment,
  HealthInsuranceCompany
} from '@/types/slovak-payroll';

/**
 * Slovak Payroll Utility Functions
 * Provides validation, formatting, and helper functions for Slovak payroll system
 */

/**
 * Validate Slovak birth number (rodné číslo)
 */
export const validateRodneCislo = (rc: string): RodneCisloValidation => {
  const errors: string[] = [];
  
  // Remove slash if present
  const cleaned = rc.replace('/', '').replace(/\s/g, '');
  
  // Basic length check
  if (cleaned.length !== 10) {
    errors.push('Rodné číslo musí mať 10 číslic');
    return { valid: false, errors, warnings: [] };
  }
  
  // Parse components
  const year = parseInt(cleaned.substr(0, 2));
  const month = parseInt(cleaned.substr(2, 2));
  const day = parseInt(cleaned.substr(4, 2));
  const sequence = parseInt(cleaned.substr(6, 4));
  
  // Determine century (simplified - assumes 1900s or 2000s)
  const fullYear = year >= 54 ? 1900 + year : 2000 + year;
  
  // Determine gender and actual month
  const isFemale = month > 50;
  const actualMonth = isFemale ? month - 50 : month;
  
  // Validate month
  if (actualMonth < 1 || actualMonth > 12) {
    errors.push('Neplatný mesiac v rodnom čísle');
  }
  
  // Validate day
  if (day < 1 || day > 31) {
    errors.push('Neplatný deň v rodnom čísle');
  }
  
  // Modulo 11 check for numbers after 1954
  if (fullYear >= 1954) {
    const num = parseInt(cleaned);
    if (num % 11 !== 0) {
      // Special case: remainder 10 and last digit is 0 is valid
      if (!(num % 11 === 10 && cleaned.endsWith('0'))) {
        errors.push('Rodné číslo neprechádza kontrolou modulo 11');
      }
    }
  }
  
  if (errors.length > 0) {
    return { valid: false, errors, warnings: [] };
  }
  
  return {
    valid: true,
    errors: [],
    warnings: [],
    parsed: {
      year: fullYear,
      month: actualMonth,
      day,
      sequence,
      gender: isFemale ? 'F' : 'M'
    }
  };
};

/**
 * Validate IBAN (Slovak format)
 */
export const validateIBAN = (iban: string): IBANValidation => {
  const errors: string[] = [];
  
  // Remove spaces and convert to uppercase
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  
  // Slovak IBAN format: SK + 2 check digits + 20 digits
  if (!cleaned.startsWith('SK')) {
    errors.push('IBAN musí začínať SK pre slovenské účty');
  }
  
  if (cleaned.length !== 24) {
    errors.push('Slovenský IBAN musí mať 24 znakov');
  }
  
  // Validate check digits using modulo 97
  if (cleaned.length === 24) {
    const rearranged = cleaned.substring(4) + cleaned.substring(0, 4);
    const numericString = rearranged.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString());
    const remainder = BigInt(numericString) % BigInt(97);
    
    if (remainder !== BigInt(1)) {
      errors.push('Neplatné kontrolné číslice IBAN');
    }
  }
  
  if (errors.length > 0) {
    return { valid: false, errors, warnings: [] };
  }
  
  // Parse components
  const checkDigits = cleaned.substring(2, 4);
  const bankCode = cleaned.substring(4, 8);
  const accountNumber = cleaned.substring(8);
  
  return {
    valid: true,
    errors: [],
    warnings: [],
    parsed: {
      countryCode: 'SK',
      checkDigits,
      bankCode,
      accountNumber
    }
  };
};

/**
 * Format amount as EUR currency
 */
export const formatEUR = (amount: number): string => {
  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Format date in Slovak format (DD.MM.YYYY)
 */
export const formatSlovakDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('sk-SK').format(d);
};

/**
 * Format period for payslip (e.g., "November 2025")
 */
export const formatPayrollPeriod = (start: string, end: string): string => {
  const endDate = new Date(end);
  const monthNames = [
    'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
    'Júl', 'August', 'September', 'Október', 'November', 'December'
  ];
  
  return `${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;
};

/**
 * Generate variable symbol for payments (format: company IČO)
 */
export const generateVariableSymbol = (companyId: string): string => {
  // In real implementation, this would use actual company IČO
  return companyId.replace(/\D/g, '').slice(0, 10);
};

/**
 * Generate specific symbol for payments (format: MMYYYY)
 */
export const generateSpecificSymbol = (date: Date): string => {
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}${year}`;
};

/**
 * Calculate working days in a month (excluding weekends and holidays)
 */
export const calculateWorkingDays = (year: number, month: number, holidays: Date[] = []): number => {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  let workingDays = 0;
  
  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
    const dayOfWeek = d.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = holidays.some(h => 
      h.getDate() === d.getDate() && 
      h.getMonth() === d.getMonth() && 
      h.getFullYear() === d.getFullYear()
    );
    
    if (!isWeekend && !isHoliday) {
      workingDays++;
    }
  }
  
  return workingDays;
};

/**
 * Generate SEPA payment instruction
 */
export const generateSEPAPayment = (
  iban: string,
  name: string,
  amount: number,
  reference: string,
  variableSymbol?: string,
  specificSymbol?: string
): SEPAPayment => {
  return {
    iban,
    name,
    amount,
    reference,
    variableSymbol,
    specificSymbol,
    message: `Mzda ${reference}`
  };
};

/**
 * Generate institutional payment details
 */
export const generateInstitutionalPayments = (
  totalSocialInsurance: number,
  healthInsuranceByCompany: Map<HealthInsuranceCompany, number>,
  totalTax: number,
  companyICO: string,
  payrollDate: Date
): InstitutionalPayment[] => {
  const payments: InstitutionalPayment[] = [];
  const specificSymbol = generateSpecificSymbol(payrollDate);
  
  // Social Insurance payment
  if (totalSocialInsurance > 0) {
    payments.push({
      institution: 'SOCIAL_INSURANCE',
      amount: totalSocialInsurance,
      variableSymbol: companyICO,
      specificSymbol,
      iban: 'SK4081800000007000138875', // Sociálna poisťovňa account
      accountName: 'Sociálna poisťovňa',
      dueDate: getPaymentDueDate(payrollDate, 'SOCIAL_INSURANCE')
    });
  }
  
  // Health Insurance payments (separate for each company)
  healthInsuranceByCompany.forEach((amount, company) => {
    if (amount > 0) {
      payments.push({
        institution: 'HEALTH_INSURANCE',
        insuranceCompany: company,
        amount,
        variableSymbol: companyICO,
        specificSymbol,
        iban: getHealthInsuranceIBAN(company),
        accountName: getHealthInsuranceAccountName(company),
        dueDate: getPaymentDueDate(payrollDate, 'HEALTH_INSURANCE')
      });
    }
  });
  
  // Tax payment
  if (totalTax > 0) {
    payments.push({
      institution: 'TAX_AUTHORITY',
      amount: totalTax,
      variableSymbol: specificSymbol, // For tax, VS is MMYYYY
      specificSymbol: '',
      iban: 'SK5081800000007000502085', // Tax authority account format
      accountName: 'Daňový úrad',
      dueDate: getPaymentDueDate(payrollDate, 'TAX_AUTHORITY')
    });
  }
  
  return payments;
};

/**
 * Get health insurance company IBAN
 */
const getHealthInsuranceIBAN = (company: HealthInsuranceCompany): string => {
  const ibans: Record<HealthInsuranceCompany, string> = {
    'VšZP': 'SK9781800000007000182629',
    'Dôvera': 'SK5181800000007000286937',
    'Union': 'SK8681800000007000496164'
  };
  return ibans[company];
};

/**
 * Get health insurance company account name
 */
const getHealthInsuranceAccountName = (company: HealthInsuranceCompany): string => {
  const names: Record<HealthInsuranceCompany, string> = {
    'VšZP': 'Všeobecná zdravotná poisťovňa',
    'Dôvera': 'Dôvera zdravotná poisťovňa',
    'Union': 'Union zdravotná poisťovňa'
  };
  return names[company];
};

/**
 * Calculate payment due date based on institution
 */
const getPaymentDueDate = (
  payrollDate: Date,
  institution: 'SOCIAL_INSURANCE' | 'HEALTH_INSURANCE' | 'TAX_AUTHORITY'
): string => {
  const dueDate = new Date(payrollDate);
  
  // All payments are due by the 8th of the following month
  dueDate.setMonth(dueDate.getMonth() + 1);
  dueDate.setDate(8);
  
  // If 8th falls on weekend, move to next business day
  while (dueDate.getDay() === 0 || dueDate.getDay() === 6) {
    dueDate.setDate(dueDate.getDate() + 1);
  }
  
  return dueDate.toISOString().split('T')[0];
};

/**
 * Generate SEPA XML file content (pain.001.001.03 format)
 */
export const generateSEPAXML = (
  payments: SEPAPayment[],
  companyName: string,
  companyIBAN: string,
  executionDate: Date
): string => {
  const messageId = `MSG-${Date.now()}`;
  const creationDateTime = new Date().toISOString();
  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
  
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.03">
  <CstmrCdtTrfInitn>
    <GrpHdr>
      <MsgId>${messageId}</MsgId>
      <CreDtTm>${creationDateTime}</CreDtTm>
      <NbOfTxs>${payments.length}</NbOfTxs>
      <CtrlSum>${totalAmount.toFixed(2)}</CtrlSum>
      <InitgPty>
        <Nm>${companyName}</Nm>
      </InitgPty>
    </GrpHdr>
    <PmtInf>
      <PmtInfId>${messageId}-1</PmtInfId>
      <PmtMtd>TRF</PmtMtd>
      <BtchBookg>false</BtchBookg>
      <NbOfTxs>${payments.length}</NbOfTxs>
      <CtrlSum>${totalAmount.toFixed(2)}</CtrlSum>
      <PmtTpInf>
        <SvcLvl>
          <Cd>SEPA</Cd>
        </SvcLvl>
      </PmtTpInf>
      <ReqdExctnDt>${executionDate.toISOString().split('T')[0]}</ReqdExctnDt>
      <Dbtr>
        <Nm>${companyName}</Nm>
      </Dbtr>
      <DbtrAcct>
        <Id>
          <IBAN>${companyIBAN}</IBAN>
        </Id>
      </DbtrAcct>
      <DbtrAgt>
        <FinInstnId>
          <BIC>GIBASKBX</BIC>
        </FinInstnId>
      </DbtrAgt>
      ${payments.map((payment, index) => `
      <CdtTrfTxInf>
        <PmtId>
          <EndToEndId>${messageId}-${index + 1}</EndToEndId>
        </PmtId>
        <Amt>
          <InstdAmt Ccy="EUR">${payment.amount.toFixed(2)}</InstdAmt>
        </Amt>
        <CdtrAgt>
          <FinInstnId>
            <BIC>GIBASKBX</BIC>
          </FinInstnId>
        </CdtrAgt>
        <Cdtr>
          <Nm>${payment.name}</Nm>
        </Cdtr>
        <CdtrAcct>
          <Id>
            <IBAN>${payment.iban}</IBAN>
          </Id>
        </CdtrAcct>
        ${payment.variableSymbol || payment.specificSymbol ? `
        <RmtInf>
          <Strd>
            <CdtrRefInf>
              ${payment.variableSymbol ? `<Ref>/VS/${payment.variableSymbol}</Ref>` : ''}
              ${payment.specificSymbol ? `<Ref>/SS/${payment.specificSymbol}</Ref>` : ''}
            </CdtrRefInf>
          </Strd>
          ${payment.message ? `<Ustrd>${payment.message}</Ustrd>` : ''}
        </RmtInf>
        ` : ''}
      </CdtTrfTxInf>`).join('')}
    </PmtInf>
  </CstmrCdtTrfInitn>
</Document>`;
  
  return xml;
};

/**
 * Export payroll data to CSV format
 */
export const exportPayrollToCSV = (payrollData: any[]): string => {
  const headers = [
    'Meno zamestnanca',
    'Rodné číslo',
    'Obdobie',
    'Hrubá mzda',
    'Sociálne poistenie',
    'Zdravotné poistenie',
    'Daň z príjmu',
    'Daňový bonus',
    'Čistá mzda',
    'K výplate',
    'Celková cena práce'
  ];
  
  const rows = payrollData.map(data => [
    data.employee.name,
    data.employee.rodne_cislo,
    formatPayrollPeriod(data.period.start, data.period.end),
    data.grossSalary.toFixed(2),
    data.socialInsurance.employee.total.toFixed(2),
    data.healthInsurance.employee.toFixed(2),
    data.taxCalculation.finalTax.toFixed(2),
    data.taxCalculation.childBonus.toFixed(2),
    data.netSalary.toFixed(2),
    data.finalPayout.toFixed(2),
    data.employerCost.toFixed(2)
  ]);
  
  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.join(';'))
  ].join('\n');
  
  // Add BOM for proper UTF-8 encoding in Excel
  return '\ufeff' + csvContent;
};

/**
 * Calculate annual income for tax purposes
 */
export const calculateAnnualIncome = (
  monthlyGross: number,
  monthsWorked: number,
  bonuses: number = 0
): number => {
  return (monthlyGross * monthsWorked) + bonuses;
};

/**
 * Check if employee qualifies for minimum wage guarantee
 */
export const checkMinimumWageCompliance = (
  monthlyGross: number,
  weeklyHours: number,
  minimumWage: number
): boolean => {
  const fullTimeEquivalent = (weeklyHours / 40) * minimumWage;
  return monthlyGross >= fullTimeEquivalent;
};